<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <div class="bg-white dark:bg-slate-900 w-full max-w-6xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ShoppingCart class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Gestionar Compra' : 'Nueva Compra' }}
          </h2>
          <p class="text-xs text-slate-500">
            {{ form.id ? `Folio: ${form.invoice_number || '---'}` : 'Registra el ingreso de mercadería.' }}
          </p>
        </div>

        <div class="flex gap-3">
          <span v-if="form.id" class="px-3 py-1 rounded-full text-xs font-bold border"
            :class="{
              'bg-emerald-100 text-emerald-700 border-emerald-200': form.status === 'COMPLETED',
              'bg-amber-50 text-amber-700 border-amber-200': form.status === 'DRAFT',
              'bg-red-50 text-red-700 border-red-200': form.status === 'CANCELLED'
            }">
            {{ getStatusLabel(form.status) }}
          </span>
          <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">

        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">

          <div class="md:col-span-1">
            <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Proveedor</label>
            <div class="relative">
              <Users class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select v-model="form.supplier" class="form-input-premium pl-10 appearance-none" :disabled="isReadOnly">
                <option value="" disabled>Seleccionar...</option>
                <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
          </div>

          <div class="md:col-span-1">
            <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Sucursal Destino</label>
            <div class="relative">
              <Building class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select v-model="form.branch" class="form-input-premium pl-10 appearance-none" :disabled="isReadOnly">
                <option value="" disabled>Seleccionar...</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
          </div>

          <div class="md:col-span-1">
            <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Fecha Compra</label>
            <div class="relative">
              <Calendar class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.purchase_date" type="date" class="form-input-premium pl-10" :disabled="isReadOnly" />
            </div>
          </div>

          <div class="md:col-span-1">
            <label class="block text-xs font-bold text-slate-500 uppercase mb-1">N° Factura</label>
            <input v-model="form.invoice_number" type="text" class="form-input-premium uppercase" placeholder="FAC-0001" :disabled="isReadOnly" />
          </div>
        </div>

        <div>
          <div class="flex justify-between items-end mb-2">
            <h3 class="text-sm font-bold text-slate-800 dark:text-slate-200">Detalle de Productos</h3>
            <button v-if="!isReadOnly" @click="addProductRow" class="text-xs font-bold text-brand-600 hover:text-brand-700 hover:underline flex items-center gap-1">
              <PlusCircle class="w-4 h-4" /> Agregar Línea
            </button>
          </div>

          <div class="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
            <table class="w-full text-xs text-left">
              <thead class="bg-slate-100 dark:bg-slate-800 text-slate-500 font-bold uppercase">
                <tr>
                  <th class="px-3 py-2 w-8">#</th>
                  <th class="px-3 py-2">Producto</th>
                  <th class="px-3 py-2 w-32">Lote</th>
                  <th class="px-3 py-2 w-32">Vencimiento</th>
                  <th class="px-3 py-2 w-28 text-right">Costo U.</th>
                  <th class="px-3 py-2 w-24 text-right">Cant.</th>
                  <th class="px-3 py-2 w-28 text-right">Total</th>
                  <th class="px-3 py-2 w-10 text-center" v-if="!isReadOnly"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                <tr v-for="(row, index) in details" :key="index" class="group hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td class="px-3 py-2 text-slate-400">{{ index + 1 }}</td>

                  <td class="px-3 py-2">
                    <select v-model="row.product" :disabled="isReadOnly" class="w-full bg-transparent border-b border-transparent focus:border-brand-500 focus:outline-none py-1 text-slate-900 dark:text-white font-medium">
                      <option value="" disabled>Buscar...</option>
                      <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }} ({{ p.code }})</option>
                    </select>
                  </td>

                  <td class="px-3 py-2">
                    <input v-model="row.batch_code" type="text" :disabled="isReadOnly" class="w-full bg-transparent border-b border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:outline-none py-1 text-xs uppercase text-center" placeholder="GENERAL" />
                  </td>

                  <td class="px-3 py-2">
                    <input v-model="row.expiration_date" type="date" :disabled="isReadOnly" class="w-full bg-transparent border-b border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:outline-none py-1 text-xs text-slate-500" />
                  </td>

                  <td class="px-3 py-2">
                    <input v-model.number="row.unit_cost" type="number" step="0.01" :disabled="isReadOnly" class="w-full bg-transparent border-b border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:outline-none py-1 text-right font-mono" placeholder="0.00" />
                  </td>

                  <td class="px-3 py-2">
                    <input v-model.number="row.quantity" type="number" step="1" :disabled="isReadOnly" class="w-full bg-transparent border-b border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:outline-none py-1 text-right font-bold text-brand-600" placeholder="1" />
                  </td>

                  <td class="px-3 py-2 text-right font-mono font-medium text-slate-700 dark:text-slate-300">
                    ${{ (row.quantity * row.unit_cost).toFixed(2) }}
                  </td>

                  <td class="px-3 py-2 text-center" v-if="!isReadOnly">
                    <button @click="removeRow(index)" class="text-slate-300 hover:text-red-500 transition-colors">
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </td>
                </tr>

                <tr v-if="details.length === 0">
                  <td :colspan="isReadOnly ? 7 : 8" class="p-8 text-center text-slate-400 border-dashed border-2 border-slate-100 dark:border-slate-800 m-4 rounded-xl">
                    <p class="text-sm">Agrega productos a la compra.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="flex justify-end">
          <div class="w-72 space-y-2 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
            <div class="flex justify-between text-sm text-slate-500">
              <span>Subtotal:</span>
              <span>${{ totals.subtotal.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-sm text-slate-500">
              <span>Impuestos (15%):</span>
              <span>${{ totals.tax.toFixed(2) }}</span>
            </div>
            <div class="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between text-lg font-bold text-slate-900 dark:text-white">
              <span>Total Compra:</span>
              <span>${{ totals.total.toFixed(2) }}</span>
            </div>
          </div>
        </div>

      </div>

      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">

        <div>
          <button
            v-if="form.id && form.status !== 'CANCELLED'"
            @click="handleCancel"
            class="text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Trash2 class="w-4 h-4" /> Anular Compra
          </button>
        </div>

        <div class="flex gap-3">
          <button @click="$emit('close')" type="button" class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors">
            Cerrar
          </button>

          <template v-if="!isReadOnly">
            <button
              @click="handleSubmit('DRAFT')"
              :disabled="isSaving"
              class="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-xl transition-colors"
            >
              Guardar Borrador
            </button>

            <button
              @click="handleSubmit('COMPLETED')"
              :disabled="isSaving"
              class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70"
            >
              <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
              <Save v-else class="w-4 h-4" />
              Procesar Entrada
            </button>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, computed, watch } from 'vue';
import {
  X, Save, Loader2, ShoppingCart, Users, Building, Calendar,
  PlusCircle, Trash2
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import Swal from 'sweetalert2';

const props = defineProps({
  show: Boolean,
  purchaseToEdit: Object
});

const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError, showWarning } = useToast();

const isSaving = ref(false);
const suppliers = ref([]);
const branches = ref([]);
const products = ref([]);

const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company;

const form = reactive({
  id: null,
  company: currentCompanyId,
  supplier: '',
  branch: '',
  user: currentUser?.id,
  purchase_date: new Date().toISOString().split('T')[0],
  invoice_number: '',
  status: 'DRAFT',
  notes: ''
});

const details = ref([]);

// Computed
const isReadOnly = computed(() => {
  return form.id && (form.status === 'COMPLETED' || form.status === 'CANCELLED');
});

const getStatusLabel = (status) => {
  switch (status) {
    case 'COMPLETED': return 'PROCESADO';
    case 'DRAFT': return 'BORRADOR';
    case 'CANCELLED': return 'ANULADO';
    default: return status;
  }
};

// Cargar Catálogos
onMounted(async () => {
  if (!currentCompanyId) return;
  try {
    const [resSupp, resBranch, resProd] = await Promise.all([
      apiClient.get(`/suppliers?company=${currentCompanyId}`),
      apiClient.get(`/branches?company=${currentCompanyId}`),
      apiClient.get(`/products?company=${currentCompanyId}`)
    ]);
    suppliers.value = resSupp.data || [];
    branches.value = resBranch.data || [];
    products.value = resProd.data || [];
  } catch (e) {
    console.error("Error cargando catálogos", e);
  }
});

// Watch Edición
watch(() => props.purchaseToEdit, async (newVal) => {
  if (newVal) {
    Object.assign(form, newVal);
    try {
      // Nota: Endpoint GET estándar para obtener detalles
      const res = await apiClient.get(`/purchase_details?purchase=${newVal.id}`);
      details.value = res.data || [];
    } catch (e) {
      console.error("Error cargando detalles", e);
      details.value = [];
    }
  } else {
    // Reset
    Object.assign(form, {
      id: null,
      company: currentCompanyId,
      supplier: '',
      branch: '',
      user: currentUser?.id,
      purchase_date: new Date().toISOString().split('T')[0],
      invoice_number: '',
      status: 'DRAFT',
      notes: ''
    });
    details.value = [];
  }
}, { immediate: true });

// Grid Logic
const addProductRow = () => {
  details.value.push({
    product: '',
    batch_code: 'GENERAL',
    expiration_date: '',
    quantity: 1,
    unit_cost: 0
  });
};

const removeRow = (index) => {
  details.value.splice(index, 1);
};

const totals = computed(() => {
  let sub = 0;
  details.value.forEach(d => {
    sub += (d.quantity || 0) * (d.unit_cost || 0);
  });
  const tax = sub * 0.15;
  return { subtotal: sub, tax: tax, total: sub + tax };
});

// --- GUARDAR (Crear o Editar) ---
const handleSubmit = async (targetStatus) => {
  if (!form.supplier || !form.branch || details.value.length === 0) {
    showWarning('Faltan datos obligatorios.');
    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      header: {
        ...form,
        status: targetStatus,
        company: currentCompanyId,
        subtotal: totals.value.subtotal,
        tax_amount: totals.value.tax,
        total: totals.value.total
      },
      lines: details.value.map(d => ({
        product: d.product,
        batch_code: d.batch_code || 'GENERAL',
        expiration_date: d.expiration_date || null,
        quantity: d.quantity,
        unit_cost: d.unit_cost,
        subtotal: d.quantity * d.unit_cost
      }))
    };

    // Si ya tiene ID, enviamos también el ID en el payload para que el backend sepa que es update
    if (form.id) {
        payload.header.id = form.id;
        // Asumiendo que tu backend transaccional soporta lógica de "Borrar y Reinsertar" detalles
        // si recibe un ID de cabecera existente.
        await apiClient.post('/purchases/transaction', payload);
        // Nota: Idealmente PUT, pero si usas el mismo endpoint transaccional,
        // el PHP debe detectar si hay ID para hacer update.
    } else {
        await apiClient.post('/purchases/transaction', payload);
    }

    showSuccess(targetStatus === 'COMPLETED' ? 'Compra procesada exitosamente.' : 'Borrador guardado.');
    emit('saved');
    emit('close');

  } catch (error) {
    console.error(error);
    const msg = error.response?.data?.error || 'Error al procesar la compra.';
    showError(msg);
  } finally {
    isSaving.value = false;
  }
};

// --- CANCELAR / ANULAR ---
const handleCancel = async () => {
  if (form.status === 'COMPLETED') {
    // Si está completada, es peligroso borrarla porque afecta stock histórico.
    // Aquí deberíamos hacer una reversión lógica.
    Swal.fire('Atención', 'Anular una compra procesada requiere reversión de stock manual (Nota de Crédito).', 'warning');
    return;
  }

  Swal.fire({
    title: '¿Anular Compra?',
    text: "El borrador será eliminado o marcado como anulado.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, anular',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        // Opción A: Borrar físico si es borrador
        await apiClient.delete(`/purchases/${form.id}`);
        // Opción B: Marcar status CANCELLED (Soft delete)
        // await apiClient.put(`/purchases/${form.id}`, { status: 'CANCELLED' });

        showSuccess('Compra anulada.');
        emit('saved');
        emit('close');
      } catch (e) {
        showError('No se pudo anular.');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
</style>